import { useState, useCallback } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import { useToast } from './useToast';
import { useTimesheetEdit } from './useTimesheetEdit';
import { validateCorrectionEdits } from '../utils/correctionValidation';
import type { PayrollRow } from './usePayrollData';

// Entrée détaillée pour le modal de correction
export interface CorrectionEntry {
  id: string;
  sheet_id: string;
  work_date: string;
  hours_normal: number;
  hours_saturday: number;
  hours_major: number;
  meals_count: number;
  indemnity_transport: number;
  indemnity_trajet: number;
  travel_zone: string | number | null;
  is_grand_deplacement: boolean;
  gd_amount: number | null;
  gd_travel_hours: number | null;
}

interface UsePayrollActionsParams {
  rows: PayrollRow[];
  startDate: string;
  endDate: string;
  periodLabel: string;
  dbPeriodStr: string;
  userId: string | undefined;
  refresh: () => void | Promise<void>;
}

export function usePayrollActions({
  rows,
  startDate,
  endDate,
  periodLabel,
  dbPeriodStr,
  userId,
  refresh,
}: UsePayrollActionsParams) {
  const { toast } = useToast();
  const { editTimeEntry } = useTimesheetEdit();

  // --- ÉTATS CORRECTION ---
  const [correctionEmployee, setCorrectionEmployee] = useState<{ empId: string; name: string } | null>(null);
  const [correctionEntries, setCorrectionEntries] = useState<CorrectionEntry[]>([]);
  const [correctionEdits, setCorrectionEdits] = useState<Record<string, Partial<CorrectionEntry>>>({});
  const [savingCorrection, setSavingCorrection] = useState(false);

  // --- EXPORTS ---
  const exportPDF = useCallback(async () => {
    const { data: leavesData } = await supabase
      .from('leaves')
      .select('employee_id, type, start_date, end_date')
      .eq('status', 'validated')
      .lte('start_date', endDate)
      .gte('end_date', startDate);

    const countDaysPDF = (s: string, e: string): number => {
      const start = new Date(s);
      const end = new Date(e);
      const from = new Date(Math.max(start.getTime(), new Date(startDate).getTime()));
      const to = new Date(Math.min(end.getTime(), new Date(endDate).getTime()));
      let days = 0;
      const d = new Date(from);
      while (d <= to) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) days++;
        d.setDate(d.getDate() + 1);
      }
      return days;
    };

    const leavesByEmployee: Record<string, Record<string, number>> = {};
    for (const leave of leavesData || []) {
      if (!leavesByEmployee[leave.employee_id]) {
        leavesByEmployee[leave.employee_id] = { cp: 0, maladie: 0, sans_solde: 0, formation: 0, autre: 0 };
      }
      leavesByEmployee[leave.employee_id][leave.type] += countDaysPDF(leave.start_date, leave.end_date);
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text(`État Préparatoire Paie`, 14, 20);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Période : ${periodLabel} (Validé uniquement)`, 14, 28);

    const totHours = parseFloat(rows.reduce((acc, r) => acc + r.totalHours, 0).toFixed(2));
    const totHS25 = parseFloat(rows.reduce((acc, r) => acc + r.overtime25, 0).toFixed(2));
    const totHS50 = parseFloat(rows.reduce((acc, r) => acc + r.overtime50, 0).toFixed(2));
    const totSunday = parseFloat(rows.reduce((acc, r) => acc + r.sundayHours, 0).toFixed(2));
    const totPaniers = rows.reduce((acc, r) => acc + r.panier, 0);
    const totTrajet = rows.reduce((acc, r) => acc + r.trajet, 0);
    const totTransport = rows.reduce((acc, r) => acc + r.transport, 0);
    const totGDDays = rows.reduce((acc, r) => acc + r.gdDaysCount, 0);
    const totGDAmount = parseFloat(rows.reduce((acc, r) => acc + r.gdTotalAmount, 0).toFixed(2));
    const totGDRoute = parseFloat(rows.reduce((acc, r) => acc + r.gdTotalTravelHours, 0).toFixed(2));
    const totZ1A = rows.reduce((acc, r) => acc + (r.zones['1A'] || 0), 0);
    const totZ1B = rows.reduce((acc, r) => acc + (r.zones['1B'] || 0), 0);
    const totZ2 = rows.reduce((acc, r) => acc + (r.zones['2'] || 0), 0);
    const totZ3 = rows.reduce((acc, r) => acc + (r.zones['3'] || 0), 0);
    const totZ4 = rows.reduce((acc, r) => acc + (r.zones['4'] || 0), 0);
    const totZ5 = rows.reduce((acc, r) => acc + (r.zones['5'] || 0), 0);
    const totZ6 = rows.reduce((acc, r) => acc + (r.zones['6'] || 0), 0);
    const totZ7 = rows.reduce((acc, r) => acc + (r.zones['7'] || 0), 0);
    const totCP = rows.reduce((acc, r) => acc + ((leavesByEmployee[r.empId]?.cp) || 0), 0);
    const totMaladie = rows.reduce((acc, r) => acc + ((leavesByEmployee[r.empId]?.maladie) || 0), 0);
    const totSansSolde = rows.reduce((acc, r) => acc + ((leavesByEmployee[r.empId]?.sans_solde) || 0), 0);
    const totFormation = rows.reduce((acc, r) => acc + ((leavesByEmployee[r.empId]?.formation) || 0), 0);
    const totAutre = rows.reduce((acc, r) => acc + ((leavesByEmployee[r.empId]?.autre) || 0), 0);

    const headers = [
      'Employé', 'Total H.', 'dont HS 25%', 'dont HS 50%', 'dont H. Dim.',
      'Trajets', 'Transp.', 'Paniers',
      'Z1A', 'Z1B', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7', 'GD',
      'GD €', 'GD Rte',
      'CP', 'Mal.', 'SS', 'Form.', 'Autre'
    ];

    autoTable(doc, {
      startY: 35,
      head: [headers],
      body: rows.map(r => {
        const leaves = leavesByEmployee[r.empId] || { cp: 0, maladie: 0, sans_solde: 0, formation: 0, autre: 0 };
        return [
          r.name,
          r.totalHours.toFixed(2),
          r.overtime25 > 0 ? r.overtime25.toFixed(2) : '-',
          r.overtime50 > 0 ? r.overtime50.toFixed(2) : '-',
          r.sundayHours > 0 ? r.sundayHours.toFixed(2) : '-',
          r.trajet,
          r.transport,
          r.panier,
          r.zones['1A'] || '-',
          r.zones['1B'] || '-',
          r.zones['2'] || '-',
          r.zones['3'] || '-',
          r.zones['4'] || '-',
          r.zones['5'] || '-',
          r.zones['6'] || '-',
          r.zones['7'] || '-',
          r.gdDaysCount > 0 ? r.gdDaysCount : '-',
          r.gdTotalAmount > 0 ? r.gdTotalAmount.toFixed(2) : '-',
          r.gdTotalTravelHours > 0 ? r.gdTotalTravelHours.toFixed(2) : '-',
          leaves.cp > 0 ? leaves.cp : '-',
          leaves.maladie > 0 ? leaves.maladie : '-',
          leaves.sans_solde > 0 ? leaves.sans_solde : '-',
          leaves.formation > 0 ? leaves.formation : '-',
          leaves.autre > 0 ? leaves.autre : '-'
        ];
      }),
      foot: [[
        'TOTAUX',
        totHours.toFixed(2),
        totHS25 > 0 ? totHS25.toFixed(2) : '-',
        totHS50 > 0 ? totHS50.toFixed(2) : '-',
        totSunday > 0 ? totSunday.toFixed(2) : '-',
        totTrajet,
        totTransport,
        totPaniers,
        totZ1A || '-',
        totZ1B || '-',
        totZ2 || '-',
        totZ3 || '-',
        totZ4 || '-',
        totZ5 || '-',
        totZ6 || '-',
        totZ7 || '-',
        totGDDays > 0 ? totGDDays : '-',
        totGDAmount > 0 ? totGDAmount.toFixed(2) : '-',
        totGDRoute > 0 ? totGDRoute.toFixed(2) : '-',
        totCP > 0 ? totCP : '-',
        totMaladie > 0 ? totMaladie : '-',
        totSansSolde > 0 ? totSansSolde : '-',
        totFormation > 0 ? totFormation : '-',
        totAutre > 0 ? totAutre : '-'
      ]],
      theme: 'grid',
      styles: { fontSize: 7, halign: 'center', cellPadding: 2 },
      headStyles: { fillColor: [0, 133, 66], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      footStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold', cellWidth: 30 },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' }
      }
    });
    doc.save(`paie_validee_${dbPeriodStr}.pdf`);
  }, [rows, startDate, endDate, periodLabel, dbPeriodStr]);

  const exportExcel = useCallback(async () => {
    const { data: leavesData } = await supabase
      .from('leaves')
      .select('employee_id, type, start_date, end_date')
      .eq('status', 'validated')
      .lte('start_date', endDate)
      .gte('end_date', startDate);

    const countDays = (s: string, e: string): number => {
      const start = new Date(s);
      const end = new Date(e);
      const from = new Date(Math.max(start.getTime(), new Date(startDate).getTime()));
      const to = new Date(Math.min(end.getTime(), new Date(endDate).getTime()));
      let days = 0;
      const d = new Date(from);
      while (d <= to) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) days++;
        d.setDate(d.getDate() + 1);
      }
      return days;
    };

    const leavesByEmployee: Record<string, Record<string, number>> = {};
    for (const leave of leavesData || []) {
      if (!leavesByEmployee[leave.employee_id]) {
        leavesByEmployee[leave.employee_id] = { cp: 0, maladie: 0, sans_solde: 0, formation: 0, autre: 0 };
      }
      leavesByEmployee[leave.employee_id][leave.type] += countDays(leave.start_date, leave.end_date);
    }

    const dataToExport = rows.map(r => {
      const leaves = leavesByEmployee[r.empId] || { cp: 0, maladie: 0, sans_solde: 0, formation: 0, autre: 0 };
      return {
        'Employé': r.name,
        'Rôle': r.role,
        'Total Heures': Number(r.totalHours.toFixed(2)),
        'HS 25%': Number(r.overtime25.toFixed(2)),
        'HS 50%': Number(r.overtime50.toFixed(2)),
        'H. Dimanche (100%)': Number(r.sundayHours.toFixed(2)),
        'Total H. Supp': Number(r.overtime.toFixed(2)),
        'Trajets': r.trajet,
        'Transport': r.transport,
        'Paniers': r.panier,
        'Zone 1A': r.zones['1A'] || 0,
        'Zone 1B': r.zones['1B'] || 0,
        'Zone 2': r.zones['2'] || 0,
        'Zone 3': r.zones['3'] || 0,
        'Zone 4': r.zones['4'] || 0,
        'Zone 5': r.zones['5'] || 0,
        'Zone 6': r.zones['6'] || 0,
        'Zone 7': r.zones['7'] || 0,
        'GD Jours': r.gdDaysCount,
        'GD Montant (€)': Number(r.gdTotalAmount.toFixed(2)),
        'GD Heures Route': Number(r.gdTotalTravelHours.toFixed(2)),
        'CP (j)': leaves.cp,
        'Maladie (j)': leaves.maladie,
        'Sans Solde (j)': leaves.sans_solde,
        'Formation (j)': leaves.formation,
        'Autre Abs. (j)': leaves.autre,
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Paie Validée");
    const wscols = [
      { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
      { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
      { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 8 },
      { wch: 10 }, { wch: 14 }, { wch: 14 },
      { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
    ];
    worksheet['!cols'] = wscols;
    XLSX.writeFile(workbook, `paie_validee_${dbPeriodStr}.xlsx`);
  }, [rows, startDate, endDate, dbPeriodStr]);

  // --- CORRECTION HANDLERS ---
  const openCorrection = useCallback(async (empId: string, empName: string) => {
    setCorrectionEmployee({ empId, name: empName });
    setCorrectionEdits({});

    const { data } = await supabase
      .from('time_entries')
      .select('id, sheet_id, work_date, hours_normal, hours_saturday, hours_major, meals_count, indemnity_transport, indemnity_trajet, travel_zone, is_grand_deplacement, gd_amount, gd_travel_hours, weekly_sheets!inner(status, employee_id)')
      .eq('weekly_sheets.employee_id', empId)
      .eq('weekly_sheets.status', 'validated')
      .gte('work_date', startDate)
      .lte('work_date', endDate)
      .order('work_date');

    if (data) {
      setCorrectionEntries(data.map(d => ({
        id: d.id,
        sheet_id: d.sheet_id,
        work_date: d.work_date,
        hours_normal: d.hours_normal,
        hours_saturday: d.hours_saturday,
        hours_major: d.hours_major,
        meals_count: d.meals_count,
        indemnity_transport: d.indemnity_transport,
        indemnity_trajet: d.indemnity_trajet,
        travel_zone: d.travel_zone,
        is_grand_deplacement: d.is_grand_deplacement || false,
        gd_amount: d.gd_amount,
        gd_travel_hours: d.gd_travel_hours
      })));
    }
  }, [startDate, endDate]);

  const updateCorrectionField = useCallback((entryId: string, field: keyof CorrectionEntry, value: unknown) => {
    setCorrectionEdits(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], [field]: value }
    }));
  }, []);

  /**
   * Valide les corrections en attente contre les règles métier critiques.
   * Délègue à la fonction pure `validateCorrectionEdits` (utils/correctionValidation.ts)
   * pour garder la logique testable unitairement.
   */
  const validateCorrections = useCallback((): string | null => {
    return validateCorrectionEdits(correctionEntries, correctionEdits);
  }, [correctionEntries, correctionEdits]);

  const saveCorrections = useCallback(async () => {
    if (!userId) return;

    // Validation métier avant envoi : règles identiques à la saisie initiale
    const validationError = validateCorrections();
    if (validationError) {
      toast.warning(validationError);
      return;
    }

    setSavingCorrection(true);

    try {
      for (const [entryId, edits] of Object.entries(correctionEdits)) {
        if (Object.keys(edits).length === 0) continue;

        const original = correctionEntries.find(e => e.id === entryId);
        if (!original) continue;

        // Ne garder que les champs réellement modifiés
        const changedFields: Record<string, unknown> = {};
        for (const [field, newVal] of Object.entries(edits)) {
          const oldVal = original[field as keyof CorrectionEntry];
          if (String(oldVal) !== String(newVal)) {
            changedFields[field] = newVal;
          }
        }

        if (Object.keys(changedFields).length === 0) continue;

        // Route l'update + l'audit log via useTimesheetEdit
        // (remplace les anciens appels à payroll_corrections et time_entries.update)
        await editTimeEntry(entryId, changedFields, {
          sheetId: original.sheet_id,
          actorPage: 'secretariat_payroll',
          reason: 'Correction secrétariat post-validation',
        });
      }

      setCorrectionEmployee(null);
      setCorrectionEdits({});
      await refresh();
    } catch (err) {
      console.error('Erreur sauvegarde correction:', err);
      toast.error('Erreur lors de la sauvegarde des corrections');
    } finally {
      setSavingCorrection(false);
    }
  }, [userId, correctionEdits, correctionEntries, refresh, toast, editTimeEntry, validateCorrections]);

  const getMergedEntry = useCallback((entry: CorrectionEntry): CorrectionEntry => {
    const edits = correctionEdits[entry.id];
    return edits ? { ...entry, ...edits } : entry;
  }, [correctionEdits]);

  const hasEdits = Object.keys(correctionEdits).length > 0;

  return {
    // Exports
    exportPDF,
    exportExcel,
    // Correction state
    correctionEmployee,
    setCorrectionEmployee,
    correctionEntries,
    correctionEdits,
    savingCorrection,
    // Correction handlers
    openCorrection,
    updateCorrectionField,
    saveCorrections,
    getMergedEntry,
    hasEdits,
  };
}
